// src/LoginPage.tsx

import React, { useState } from 'react';
import { TextField, Container, Typography, Box, Tabs, Tab, useTheme, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { PrimaryButton } from './components';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LoginPage: React.FC = () => {
  const authContext = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [nome, setNome] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const handleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      await authContext.signIn(email, senha);
      navigate('/tw/dashboard');
    } catch (err: any) {
      setError(`${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    setSuccess(null);

    if (!nome || nome.length < 3) {
      setError("Nome deve ter pelo menos 3 caracteres.");
      return;
    }

    if (!email || !email.includes('@')) {
      setError("Por favor, informe um email válido.");
      return;
    }

    if (!senha || senha.length < 6) {
      setError("Senha deve ter pelo menos 6 caracteres.");
      return;
    }

    setLoading(true);
    try {
      await authContext.signUp(nome, email, senha);
      // Por favor, verifique seu email para confirmar o registro.
      setSuccess("Cadastro realizado com sucesso!");
      setNome('');
      setEmail('');
      setSenha('');
    } catch (err: any) {
      setError(err.message || "Ocorreu um erro ao criar a conta. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === "Enter") {
      event.preventDefault();
      action();
    }
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url("/landing-page-background.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3
      }}
    >
      <Container
        maxWidth="xs"
        sx={{
          backgroundColor: '#1F2C40',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          p: 4
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box
            component="img"
            src="/logo192.png"
            alt="ThinkWay"
            sx={{
              width: 120,
              height: 'auto',
              mb: 2,
              filter: 'brightness(1.2)'
            }}
          />

          {/* Mensagem dinâmica no topo */}
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 600,
              mb: 3,
              textAlign: "center",
            }}
          >
            {tabValue === 0 ? "Bem-vindo de volta!" : "Cadastre sua empresa!"}
          </Typography>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="login tabs"
            centered
            variant="fullWidth"
            sx={{
              width: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              '& .MuiTabs-indicator': {
                height: 4,
                borderRadius: '2px',
                backgroundColor: '#ffffff'
              },
              '& .MuiTab-root': {
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '1rem',
                color: '#ffffff',
                transition: 'all 0.2s',
                '&.Mui-selected': {
                  color: '#ffffff', // força branco na tab ativa
                  fontWeight: 600,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }
            }}
          >
            <Tab label="Login" />
            <Tab label="Cadastro" />
          </Tabs>

          {error && (
            <Typography
              sx={{
                color: theme.palette.error.contrastText,
                bgcolor: theme.palette.secondary.main,
                p: 1.5,
                borderRadius: '8px',
                width: '100%',
                mt: 3,
                fontSize: '0.875rem',
                textAlign: 'center'
              }}
            >
              {error}
            </Typography>
          )}

          {success && (
            <Typography
              sx={{
                color: theme.palette.success.dark,
                bgcolor: theme.palette.success.light,
                p: 1.5,
                borderRadius: '8px',
                width: '100%',
                mt: 3,
                fontSize: '0.875rem',
                textAlign: 'center'
              }}
            >
              {success}
            </Typography>
          )}

          <TabPanel value={tabValue} index={0}>
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              color='secondary'
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, handleLogin)}
              sx={{
                mb: 2,
                color: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  color: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: theme.palette.secondary.main,
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'white',
                  opacity: 1
                }
              }}
            />
            <TextField
              label="Senha"
              variant="outlined"
              type={showPassword ? "text" : "password"}  // alterna entre texto e senha
              fullWidth
              color="secondary"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, handleLogin)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "white" }}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 20, mr: 1 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 20, mr: 1 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                color: "white",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  color: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.secondary.main,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-focused": {
                    color: theme.palette.secondary.main,
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "white",
                  opacity: 1,
                },
              }}
            />
            <PrimaryButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleLogin}
              disabled={loading}
              sx={{ py: 1 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Entrar"}
            </PrimaryButton>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <TextField
              label="Nome da empresa"
              variant="outlined"
              fullWidth
              color='secondary'
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              sx={{
                mb: 2,
                color: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  color: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: theme.palette.secondary.main,
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'white',
                  opacity: 1
                }
              }}
            />
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              color='secondary'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 2,
                color: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  color: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: theme.palette.secondary.main,
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'white',
                  opacity: 1
                }
              }}
            />
            <TextField
              label="Senha"
              variant="outlined"
              type={showRegisterPassword ? "text" : "password"}
              fullWidth
              color="secondary"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, handleRegister)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      edge="end"
                      sx={{ color: "white" }}
                    >
                      {showRegisterPassword ? (
                        <VisibilityOff sx={{ fontSize: 20, mr: 1 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 20, mr: 1 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                color: "white",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  color: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.secondary.main,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-focused": {
                    color: theme.palette.secondary.main,
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "white",
                  opacity: 1,
                },
              }}
            />
            <PrimaryButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleRegister}
              disabled={loading}
              sx={{ py: 1 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Criar conta"}
            </PrimaryButton>
          </TabPanel>
        </Box>



      </Container>
    </Container>
  );
};

export default LoginPage;